import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { useLogout } from '@/hooks/useLogout';
import HoverDropdown from '../HoverDropdown/HoverDropdown';
import { ROLES } from '@/core/constants';
import './Avatar.scss';
import { Participant } from './Participants';
import { getColor } from './Participants.helpers';
import Button from '../Button';
import { AuthedRoutesEnum } from '@/core/enums';
import { Link } from 'react-router';
import { useAppSelector } from '@/hooks';

interface Props {
  data: Participant;
  className?: string;
  backgroundColor: string;
  testId?: string;
  isUser?: boolean;
}

export function AvatarWithDropdown({
  data,
  className = '',
  backgroundColor,
  testId,
  isUser = false,
}: PropsWithChildren<Props>) {
  const cognitoUser = useAppSelector((s) => s.user.cognitoUser);
  const isMfaEnabled = !!cognitoUser?.UserMFASettingList?.length;

  const { t } = useTranslation();
  const { onLogoutClick } = useLogout();
  return (
    <HoverDropdown
      arrow={false}
      trigger={
        <div
          className={`flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-full bg-[#2722F8] text-sm uppercase leading-none text-white ${className}`}
          data-testid='profile-dropdown'
          style={{
            backgroundColor: getColor(data.Name),
          }}
        >
          {data.Name[0]}
          {data.FamilyName[0]}
        </div>
      }
      menuClassName='!left-[unset] -right-4 !translate-x-0'
    >
      <div className='absolute right-5 top-0 z-[101] h-0 w-0 border-b-[12px] border-l-[12px] border-r-[12px] border-b-white border-l-transparent border-r-transparent' />
      <div className='mt-3 space-y-4 p-3 md:min-w-[285px]'>
        <div className='flex items-start justify-between'>
          <div
            className={`Avatar flex items-center justify-center rounded-full uppercase text-white ${className}`}
            style={{
              backgroundColor,
            }}
            data-testid={testId}
          >
            {data.Name[0]}
            {data.FamilyName[0]}
          </div>
          <div className='ml-2 flex-grow truncate'>
            <div className='truncate font-semibold'>
              {data.Name} {data.FamilyName}
            </div>
            {!isUser && (
              <div className='text-sm text-blue-700'>{ROLES[data.Role]}</div>
            )}
            <div className='truncate text-sm text-blue-700'>{data.Email}</div>
          </div>
        </div>
        <div className={isUser ? 'border-b pb-4' : undefined}>
          <div className='mb-2 font-semibold'>
            <span>{t('2-step authentication')}</span>&nbsp;
            <span>{isMfaEnabled ? t('activated') : t('not activated')}</span>
          </div>
          <Link
            to={AuthedRoutesEnum.Profile}
            className='Button--secondary Button--sm flex w-full items-center justify-center'
            data-testid='manage-mfa-button'
          >
            {t('Manage')}
          </Link>
        </div>
        {isUser && (
          <Button
            size='sm'
            styling='outline'
            className='!w-full whitespace-nowrap text-center no-underline'
            onClick={onLogoutClick}
            tabIndex={-1}
          >
            {t('Log Out')}
          </Button>
        )}
      </div>
    </HoverDropdown>
  );
}
